import React from "react";
import { compose, withPropsOnChange, withProps } from "recompose";
import { Page, onMount, PendingButton } from "components/common";
import { map, keys } from "lodash";
import { withFormik, Form, Field, useFormikContext } from "formik";
import FormGroup from "./FormGroup";
import BaseSelect from "react-select";
import {
  UncontrolledButtonDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { Trans } from "react-i18next";
import { NotificationManager } from "react-notifications";
import { mappings } from "services/templates";
import BackButton from "components/refactoring/BackButton";
import { languageNameMapping } from "services/util";

const Select = ({ name, fieldProps, ...props }) => {
const { setFieldValue, setFieldTouched } = useFormikContext();

  return (
    <Field
      name={name}
      render={({ field }) => (
        <BaseSelect
          {...field}
          onChange={(e) => setFieldValue(field.name, e)}
          onBlur={(e) => setFieldTouched(field.name, e)}
          {...props}
        />
      )}
      {...fieldProps}
    />
  );
};

class EmailTemplateEdit extends React.Component {
  subjectRef = React.createRef();
  contentRef = React.createRef();
  lastBlur = "subject";

  onInsertPlaceholder = (placeholder) => {
    const { values, setFieldValue } = this.props;
    const string = values[this.lastBlur] || "";
    const index = this[`${this.lastBlur}Ref`].current.selectionStart || 0;
    const newString = [
      string.slice(0, index),
      `[${placeholder}]`,
      string.slice(index),
    ].join("");
    setFieldValue(this.lastBlur, newString);
  };
  render = () => {
    const { types, typeOptions, values, isSubmitting, isValid, placeholders } =
      this.props;
    const { onInsertPlaceholder } = this;

    const languages_list = Object.keys(languageNameMapping).map((i) => ({ value: i, label: languageNameMapping[i] }))

    return (
      <Page
        title={
          values.id ? `${types[values.type]} Email Template` : "Email Template"
        }
      >
        <BackButton />
        <Form>
          <FormGroup label="Name" name="name">
            <Field name="name" className="form-control"/>
          </FormGroup>
          
          <FormGroup label="Language" name="language" className="my-2">
            <Select
              options={languages_list}
              clearable={false}
              simpleValue
              name="language"
            />
            </FormGroup>

          {!values.id && (
            <FormGroup label="Type" name="type" className="pt-2 pb-2">
              <Select
                options={typeOptions}
                clearable={false}
                simpleValue
                name="type"
              />
            </FormGroup>
          )}
          <FormGroup name="is_default" label="Default" check className="my-2">
            <Field
              name="is_default"
              render={({ field }) => (
                <input
                  {...field}
                  type="checkbox"
                  className="form-check-input"
                  disabled={values.is_system}
                  checked={values.is_default}
                />
              )}
            />
          </FormGroup>
          <hr />
          <div className="d-flex">
            <label>
              <Trans>Message</Trans>
            </label>
            <div className="mx-auto" />
            {placeholders && (
              <UncontrolledButtonDropdown>
                <DropdownToggle caret color="link" className="btn-sm">
                  <Trans>Insert placeholder</Trans>
                </DropdownToggle>
                <DropdownMenu>
                  {placeholders.map((i) => (
                    <DropdownItem
                      key={i}
                      onClick={() => onInsertPlaceholder(i)}
                    >
                      {i}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledButtonDropdown>
            )}
          </div>
          <FormGroup name="subject">
            <Field
              name="subject"
              render={({ field }) => (
                <input
                  ref={this.subjectRef}
                  {...field}
                  className="form-control"
                  onBlur={(...args) => {
                    this.lastBlur = "subject";
                    return field.onBlur(...args);
                  }}
                />
              )}
            />
          </FormGroup>
          <FormGroup name="content" className="mt-1">
            <Field 
              name="content"
              render={({ field }) => (
                <textarea
                  rows="10"
                  ref={this.contentRef}
                  className="form-control"
                  {...field}
                  onBlur={(...args) => {
                    this.lastBlur = "content";
                    return field.onBlur(...args);
                  }}
                />
              )}
            />
          </FormGroup>
          <div className="d-flex justify-content-end mt-2">
            <PendingButton
              disabled={!isValid}
              pending={isSubmitting}
              color="success"
              type="submit"
            >
              Save
            </PendingButton>
          </div>
        </Form>
      </Page>
    );
  };
}

const enhance = compose(
  onMount(({ match, xero, t }) => () => {
    const { id } = match.params;
    const isNew = id === "new";

    return Promise.props({
      state: isNew
        ? {
            name: "",
            subject: "",
            content: "",
            language:"",
          }
        : xero.get(`email_templates/${id}`).get("data"),
      isNew,
      id,
      types: xero.get("email_templates/types").get("data"),
    });
  }),
  withPropsOnChange(["types"], ({ types, t }) => ({
    typeOptions: map(types, (label, value) => ({ value, label: t(label) })),
  })),
  withFormik({
    isInitialValid: ({ isNew }) => !isNew,
    handleSubmit: (
      values,
      { setSubmitting, props: { xero, isNew, id, t, history } }
    ) => {
      return (
        isNew
          ? xero.post("email_templates", values)
          : xero.patch(`email_templates/${id}`, values)
      )
        .then(() => {
          NotificationManager.success(t("Saved successfully"));
          history.pushUp();
        })
        .finally(() => setSubmitting(false));
    },
    mapPropsToValues: (props) => props.state,
    validate: (values) => {
      const errors = {};

      if (!values.name) errors.name = true;
      if (!values.type) errors.type = true;
      if (!values.subject) errors.subject = true;
      if (!values.content) errors.content = true;
      if (!values.language  ) errors.language  = true;

      return errors;
    },
  }),
  withProps(({ values }) => ({
    placeholders: mappings[values.type] ? keys(mappings[values.type]) : null,
  }))
);

export default enhance(EmailTemplateEdit);
